/* Bloggod animation + widget runtime — extracted from article bodies because
 * Shopify strips closing </script> tags from article body content, breaking
 * inline JS. This script detects ANY [class^="bloggod-"] wrapper and runs the
 * standard count-up + reveal + progress + back-to-top animations PLUS the
 * shared interactive widgets (tabs, mode toggles, quiz options, sample chips,
 * timeline cards, range-slider calculators).
 */
(function(){
  'use strict';
  if (window.__bloggodInit) return;
  window.__bloggodInit = true;

  var prefersNoMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── Tab / tablist behaviour ───────────────────────────────────────────
  // Any [role="tab"] inside a [role="tablist"] toggles aria-selected + .bg-active
  // on its siblings. If the tab has aria-controls, it shows that panel and
  // hides the other panels in the same group (siblings sharing tablist parent).
  function bindTabs(root){
    root.querySelectorAll('[role="tablist"]').forEach(function(list){
      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      tabs.forEach(function(tab){
        if (tab.__bgBound) return;
        tab.__bgBound = true;
        tab.addEventListener('click', function(e){
          e.preventDefault();
          tabs.forEach(function(t){
            t.setAttribute('aria-selected', 'false');
            t.classList.remove('bg-active');
          });
          tab.setAttribute('aria-selected', 'true');
          tab.classList.add('bg-active');
          var panelId = tab.getAttribute('aria-controls');
          if (panelId){
            // hide other panels in same group; show this one
            var owner = list.parentElement || document;
            var allPanels = owner.querySelectorAll('[role="tabpanel"]');
            allPanels.forEach(function(p){
              p.hidden = (p.id !== panelId);
              p.classList.toggle('bg-active', p.id === panelId);
            });
          }
          // Custom event for article-specific logic
          tab.dispatchEvent(new CustomEvent('bg:tab', { bubbles: true, detail: { tab: tab } }));
        });
      });
    });
  }

  // ─── Sample / chip click → fills a target input or output ──────────────
  // <button data-sample="VALUE" data-target="#someInput">VALUE</button>
  // OR <button data-sample="VALUE">VALUE</button> with a sibling
  // [data-sample-output] inside the same parent group.
  function bindSampleChips(root){
    root.querySelectorAll('[data-sample]').forEach(function(btn){
      if (btn.__bgBound) return;
      btn.__bgBound = true;
      btn.addEventListener('click', function(e){
        e.preventDefault();
        var val = btn.getAttribute('data-sample');
        var sel = btn.getAttribute('data-target');
        var target = sel ? document.querySelector(sel) : null;
        if (!target){
          var group = btn.closest('[data-sample-group]') || btn.parentElement;
          target = group && (group.querySelector('[data-sample-output]') || group.querySelector('input[type="text"], input[type="search"]'));
        }
        if (target){
          if ('value' in target){
            target.value = val;
            target.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            target.textContent = val;
          }
        }
        // visual selection state
        var siblings = btn.parentElement ? btn.parentElement.querySelectorAll('[data-sample]') : [];
        siblings.forEach(function(s){ s.classList.remove('bg-active'); s.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('bg-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.dispatchEvent(new CustomEvent('bg:sample', { bubbles: true, detail: { value: val } }));
      });
    });
  }

  // ─── Quiz option (.bg-qopt with data-key + data-val) ───────────────────
  // Selects one option per data-key group within the quiz container.
  function bindQuizOptions(root){
    root.querySelectorAll('.bg-qopt').forEach(function(btn){
      if (btn.__bgBound) return;
      btn.__bgBound = true;
      btn.addEventListener('click', function(e){
        e.preventDefault();
        var key = btn.getAttribute('data-key');
        var val = btn.getAttribute('data-val');
        var quiz = btn.closest('.bg-quiz, [data-quiz]') || btn.parentElement;
        if (quiz){
          var group = quiz.querySelectorAll('.bg-qopt[data-key="' + key + '"]');
          group.forEach(function(o){ o.classList.remove('bg-active'); o.setAttribute('aria-pressed', 'false'); });
          quiz.dataset['answer_' + key] = val;
        }
        btn.classList.add('bg-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.dispatchEvent(new CustomEvent('bg:quiz', { bubbles: true, detail: { key: key, val: val } }));
        // Recommendation hint: if quiz has all keys filled, fire bg:quiz-complete
        if (quiz){
          var keys = Array.prototype.slice.call(quiz.querySelectorAll('.bg-qopt')).reduce(function(acc, o){ var k = o.getAttribute('data-key'); if (k && !acc.includes(k)) acc.push(k); return acc; }, []);
          var allFilled = keys.every(function(k){ return quiz.dataset['answer_' + k]; });
          if (allFilled){
            var answers = {};
            keys.forEach(function(k){ answers[k] = quiz.dataset['answer_' + k]; });
            quiz.dispatchEvent(new CustomEvent('bg:quiz-complete', { bubbles: true, detail: { answers: answers } }));
          }
        }
      });
    });
  }

  // ─── Timeline card (data-tl) ───────────────────────────────────────────
  // Mark clicked card aria-selected, deselect siblings, dispatch event so
  // detail panel can swap content.
  function bindTimeline(root){
    var cards = root.querySelectorAll('[data-tl]');
    if (!cards.length) return;
    cards.forEach(function(card){
      if (card.__bgBound) return;
      card.__bgBound = true;
      card.addEventListener('click', function(e){
        e.preventDefault();
        cards.forEach(function(c){ c.setAttribute('aria-selected', 'false'); c.classList.remove('bg-active'); });
        card.setAttribute('aria-selected', 'true');
        card.classList.add('bg-active');
        card.dispatchEvent(new CustomEvent('bg:tl', { bubbles: true, detail: { tl: card.getAttribute('data-tl') } }));
      });
    });
  }

  // ─── Range slider live readout ─────────────────────────────────────────
  // <input type="range" id="bg-w-pages"> + <output for="bg-w-pages">…</output>
  // OR <input type="range" data-output="#someEl"> updates that element's text.
  function bindSliders(root){
    root.querySelectorAll('input[type="range"]').forEach(function(input){
      if (input.__bgBound) return;
      input.__bgBound = true;
      function update(){
        var sel = input.getAttribute('data-output');
        var out = sel ? document.querySelector(sel) : null;
        if (!out){
          var byFor = input.id ? root.querySelector('output[for="' + input.id + '"]') : null;
          out = byFor || (input.parentElement && input.parentElement.querySelector('output'));
        }
        if (out) out.textContent = input.value;
        input.dispatchEvent(new CustomEvent('bg:slider', { bubbles: true, detail: { id: input.id, value: input.value } }));
      }
      input.addEventListener('input', update, { passive: true });
      update();
    });
  }

  // ─── Article-specific calculators ──────────────────────────────────────
  // Centralised here because Shopify strips </script> from article bodies.
  // Article match by [class*="bloggod-<slug>"] root.
  function articleSpecific(root){
    var rootClass = root.className || '';

    // impresora-barata-para-casa: slider drives 12-month cost estimate
    if (rootClass.indexOf('bloggod-impresora-barata') !== -1){
      var pages = root.querySelector('#bg-w-pages');
      var costOut = root.querySelector('#bg-w-cost-12m, [data-out="cost-12m"]');
      var savingsOut = root.querySelector('#bg-w-savings, [data-out="savings"]');
      if (pages){
        var calc = function(){
          var p = parseFloat(pages.value || '0');
          // Average OEM cost ~0.06 €/page, compatible ~0.018 €/page (3-4× cheaper)
          var oemY = (p * 12 * 0.06).toFixed(0);
          var compY = (p * 12 * 0.018).toFixed(0);
          var save = (p * 12 * 0.042).toFixed(0);
          if (costOut) costOut.textContent = compY + ' €';
          if (savingsOut) savingsOut.textContent = save + ' €';
          var oemOut = root.querySelector('#bg-w-cost-oem, [data-out="cost-oem"]');
          if (oemOut) oemOut.textContent = oemY + ' €';
        };
        pages.addEventListener('input', calc, { passive: true });
        calc();
      }
    }

    // epson-ecotank-vs-canon-megatank-vs-hp-smart-tank: ITS printer selector.
    // Article wrapper class is bloggod-tanks. Widget asks pages/month, color
    // %, budget, brand preference; recommends one of three tank printers and
    // calculates payback vs cartridge printer.
    if (rootClass.indexOf('bloggod-tanks') !== -1){
      var pagesInp = root.querySelector('#tank-pages');
      var colorInp = root.querySelector('#tank-color');
      var colorVal = root.querySelector('#tank-color-v');
      var budgetInp = root.querySelector('#tank-budget');
      var brandSel = root.querySelector('#tank-brand');
      var goBtn = root.querySelector('#tank-go');
      var result = root.querySelector('#tank-result');
      if (pagesInp && colorInp && goBtn && result){
        // Live slider readout
        var updateColor = function(){
          if (colorVal) colorVal.textContent = colorInp.value + '%';
          colorInp.style.setProperty('--pct', colorInp.value + '%');
        };
        colorInp.addEventListener('input', updateColor, { passive: true });
        updateColor();

        var MODELS = {
          epson: { name:'Epson EcoTank ET-2870',  price:179.99, cpp:0.004,  url:'/collections/cartuchos-tinta-epson-compatible' },
          canon: { name:'Canon PIXMA G3570',       price:285.83, cpp:0.0045, url:'/collections/cartuchos-tinta-canon' },
          hp:    { name:'HP Smart Tank 7001',      price:219.00, cpp:0.005,  url:'/collections/cartuchos-tinta-hp' }
        };

        function calcTanks(){
          var pages = parseFloat(pagesInp.value || '180');
          var color = parseFloat(colorInp.value || '30');
          var budget = parseFloat(budgetInp ? budgetInp.value : '250');
          var brand = brandSel ? brandSel.value : 'any';
          var pick, why;
          if (brand !== 'any' && MODELS[brand]){
            pick = MODELS[brand];
            why = 'Has elegido preferencia de marca. Este es el modelo de entrada de la gama tank con mejor relación calidad-precio en abril de 2026, según rendimiento ISO/IEC 24711 y precios verificados.';
          } else if (budget < 200){
            pick = MODELS.epson;
            why = 'Con tu presupuesto, EcoTank ET-2870 es la única opción tank por debajo de 200 €. Mantiene resolución 5.760×1.440 dpi y coste por página equivalente al resto de la gama.';
          } else if (pages > 250 || color > 60){
            pick = MODELS.hp;
            why = (pages > 250 ? 'Con un volumen de ' + Math.round(pages) + ' páginas/mes' : 'Con un ' + Math.round(color) + '% de impresión en color') + ', HP Smart Tank 7001 gana por velocidad (15 ppm B / 9 ppm C) y dúplex automático que reduce papel y tiempo.';
          } else if (budget >= 280){
            pick = MODELS.canon;
            why = 'Canon PIXMA G3570 incluye ADF de 20 hojas para escaneo masivo y mantiene calidad MegaTank a un precio premium, ideal si combinas impresión y digitalización.';
          } else {
            pick = MODELS.epson;
            why = 'EcoTank ET-2870 es el equilibrio entre precio (179,99 €), calidad fotográfica (5.760 dpi) y bajo coste por página (~0,004 €) para tu volumen de ' + Math.round(pages) + ' páginas/mes.';
          }
          // Cost vs cartridge printer at ~0.06 €/page
          var monthlySaving = pages * (0.06 - pick.cpp);
          var paybackM = pick.price / Math.max(monthlySaving, 0.01);
          var paybackStr = paybackM < 1 ? 'menos de 1 mes'
                          : paybackM < 12 ? Math.ceil(paybackM) + ' meses'
                          : (paybackM / 12).toFixed(1).replace('.', ',') + ' años';
          var titleEl = root.querySelector('#tank-res-title');
          var whyEl = root.querySelector('#tank-res-why');
          var priceEl = root.querySelector('#tank-res-price');
          var cppEl = root.querySelector('#tank-res-cpp');
          var paybackEl = root.querySelector('#tank-res-payback');
          var ctaEl = root.querySelector('#tank-res-cta');
          if (titleEl) titleEl.textContent = pick.name;
          if (whyEl) whyEl.textContent = why;
          if (priceEl) priceEl.textContent = pick.price.toFixed(2).replace('.', ',') + ' €';
          if (cppEl) cppEl.textContent = (pick.cpp * 100).toFixed(1).replace('.', ',') + ' c€/pág';
          if (paybackEl) paybackEl.textContent = paybackStr;
          if (ctaEl){ ctaEl.href = pick.url; ctaEl.textContent = 'Ver tinta compatible ' + pick.name.split(' ')[0] + ' →'; }
          result.classList.add('bg-show');
          try { result.focus({ preventScroll: true }); } catch(_){}
        }

        goBtn.addEventListener('click', function(e){ e.preventDefault(); calcTanks(); });
      }
    }

    // tinta-guia: cartridge code decoder. Looks up brand/family/color/XL/
    // compatible printers + buy-link for known codes. The widget HTML has
    // chips with data-sample (already wired by bindSampleChips to fill the
    // input) plus a #bg-decoder-btn that needs custom decode logic.
    if (rootClass.indexOf('bloggod-tinta-guia') !== -1){
      var dInput = root.querySelector('#bg-decoder-input');
      var dBtn = root.querySelector('#bg-decoder-btn');
      var dResult = root.querySelector('#bg-decoder-result');
      if (dInput && dBtn && dResult){
        // Cartridge code database. Each key is the canonical code; values
        // contain enough to render a useful ficha. Generated from product
        // catalog 2026-05; covers the chip examples + common variants.
        var DB = {
          // ── HP ──
          'HP 301':    { brand:'HP', name:'HP 301',    color:'Tricolor + Negro', xl:false, yield:'190 p (negro), 165 p (color)', printers:'DeskJet 1050, 2050, 3050; OfficeJet 2620, 4630', url:'/collections/cartuchos-tinta-hp' },
          'HP 301XL':  { brand:'HP', name:'HP 301XL',  color:'Tricolor + Negro', xl:true,  yield:'480 p (negro), 330 p (color)', printers:'DeskJet 1050, 2050, 3050; OfficeJet 2620, 4630', url:'/collections/cartuchos-tinta-hp' },
          'HP 302':    { brand:'HP', name:'HP 302',    color:'Tricolor + Negro', xl:false, yield:'190 p (negro), 165 p (color)', printers:'DeskJet 1110, 2130, 3630; OfficeJet 3830, 4650', url:'/collections/cartuchos-tinta-hp' },
          'HP 302XL':  { brand:'HP', name:'HP 302XL',  color:'Tricolor + Negro', xl:true,  yield:'480 p (negro), 330 p (color)', printers:'DeskJet 1110, 2130, 3630; OfficeJet 3830, 4650', url:'/collections/cartuchos-tinta-hp' },
          'HP 304':    { brand:'HP', name:'HP 304',    color:'Tricolor + Negro', xl:false, yield:'120 p (negro), 100 p (color)', printers:'DeskJet 2620, 2630, 3720, 3730, 3735', url:'/collections/cartuchos-tinta-hp' },
          'HP 304XL':  { brand:'HP', name:'HP 304XL',  color:'Tricolor + Negro', xl:true,  yield:'300 p (negro), 330 p (color)', printers:'DeskJet 2620, 2630, 3720, 3730, 3735', url:'/collections/cartuchos-tinta-hp' },
          'HP 305':    { brand:'HP', name:'HP 305',    color:'Tricolor + Negro', xl:false, yield:'120 p (negro), 100 p (color)', printers:'DeskJet 2710, 2720e, 4110e; Envy 6010, 6020', url:'/collections/cartuchos-tinta-hp' },
          'HP 305XL':  { brand:'HP', name:'HP 305XL',  color:'Tricolor + Negro', xl:true,  yield:'240 p (negro), 200 p (color)', printers:'DeskJet 2710, 2720e, 4110e; Envy 6010, 6020', url:'/collections/cartuchos-tinta-hp' },
          'HP 364':    { brand:'HP', name:'HP 364',    color:'4 colores (CMYK + foto)', xl:false, yield:'~250 p por color', printers:'Photosmart 5510, 7510; OfficeJet 4620', url:'/collections/cartuchos-tinta-hp' },
          'HP 364XL':  { brand:'HP', name:'HP 364XL',  color:'4 colores (CMYK + foto)', xl:true,  yield:'~550 p por color', printers:'Photosmart 5510, 7510; OfficeJet 4620', url:'/collections/cartuchos-tinta-hp' },
          'HP 903':    { brand:'HP', name:'HP 903',    color:'4 colores (CMYK)',        xl:false, yield:'~315 p por color', printers:'OfficeJet Pro 6960, 6970, 6975', url:'/collections/cartuchos-tinta-hp' },
          'HP 903XL':  { brand:'HP', name:'HP 903XL',  color:'4 colores (CMYK)',        xl:true,  yield:'~825 p por color', printers:'OfficeJet Pro 6960, 6970, 6975', url:'/collections/cartuchos-tinta-hp' },
          'HP 953':    { brand:'HP', name:'HP 953',    color:'4 colores (CMYK)',        xl:false, yield:'~900 p (negro), ~700 (color)', printers:'OfficeJet Pro 7720, 7730, 8210, 8710', url:'/collections/cartuchos-tinta-hp' },
          'HP 953XL':  { brand:'HP', name:'HP 953XL',  color:'4 colores (CMYK)',        xl:true,  yield:'~2.000 p (negro), ~1.600 (color)', printers:'OfficeJet Pro 7720, 7730, 8210, 8710', url:'/collections/cartuchos-tinta-hp' },
          // ── Canon (PG = Pigment black, CL = ChromaLife color) ──
          'PG-540':    { brand:'Canon', name:'PG-540',    color:'Negro',     xl:false, yield:'~180 p', printers:'PIXMA MG2150, 2250, 3550, 3650; MX375, 395', url:'/collections/cartuchos-tinta-canon' },
          'PG-540XL':  { brand:'Canon', name:'PG-540XL',  color:'Negro',     xl:true,  yield:'~600 p', printers:'PIXMA MG2150, 2250, 3550, 3650; MX375, 395', url:'/collections/cartuchos-tinta-canon' },
          'CL-541':    { brand:'Canon', name:'CL-541',    color:'Tricolor',  xl:false, yield:'~180 p', printers:'PIXMA MG2150, 2250, 3550, 3650; MX375, 395', url:'/collections/cartuchos-tinta-canon' },
          'CL-541XL':  { brand:'Canon', name:'CL-541XL',  color:'Tricolor',  xl:true,  yield:'~400 p', printers:'PIXMA MG2150, 2250, 3550, 3650; MX375, 395', url:'/collections/cartuchos-tinta-canon' },
          'PG-545':    { brand:'Canon', name:'PG-545',    color:'Negro',     xl:false, yield:'~180 p', printers:'PIXMA TS3150, 3151, 3350, 3450, 3451; MG2450, 2950', url:'/collections/cartuchos-tinta-canon' },
          'PG-545XL':  { brand:'Canon', name:'PG-545XL',  color:'Negro',     xl:true,  yield:'~400 p', printers:'PIXMA TS3150, 3151, 3350, 3450, 3451; MG2450, 2950', url:'/collections/cartuchos-tinta-canon' },
          'CL-546':    { brand:'Canon', name:'CL-546',    color:'Tricolor',  xl:false, yield:'~180 p', printers:'PIXMA TS3150, 3151, 3350, 3450, 3451; MG2450, 2950', url:'/collections/cartuchos-tinta-canon' },
          'CL-546XL':  { brand:'Canon', name:'CL-546XL',  color:'Tricolor',  xl:true,  yield:'~300 p', printers:'PIXMA TS3150, 3151, 3350, 3450, 3451; MG2450, 2950', url:'/collections/cartuchos-tinta-canon' },
          // ── Epson ──
          'T502':      { brand:'Epson', name:'T502',      color:'Multipack CMYK', xl:false, yield:'~210 p por color', printers:'EcoTank ET-2700, 2750, 3700, 3750, 4750', url:'/collections/cartuchos-tinta-epson-compatible' },
          'T502XL':    { brand:'Epson', name:'T502XL',    color:'Multipack CMYK', xl:true,  yield:'~550 p por color', printers:'EcoTank ET-2700, 2750, 3700, 3750, 4750', url:'/collections/cartuchos-tinta-epson-compatible' },
          'T603':      { brand:'Epson', name:'T603',      color:'Multipack CMYK', xl:false, yield:'~150 p por color', printers:'Expression Home XP-2100, 2105, 3100, 3105, 4100, 4105', url:'/collections/cartuchos-tinta-epson-compatible' },
          'T603XL':    { brand:'Epson', name:'T603XL',    color:'Multipack CMYK', xl:true,  yield:'~500 p por color', printers:'Expression Home XP-2100, 2105, 3100, 3105, 4100, 4105', url:'/collections/cartuchos-tinta-epson-compatible' },
          // ── Brother ──
          'LC3211':    { brand:'Brother', name:'LC3211',    color:'Multipack CMYK', xl:false, yield:'~200 p por color', printers:'DCP-J772DW, J774DW; MFC-J890DW, J895DW', url:'/collections/cartuchos-tinta-brother' },
          'LC3213':    { brand:'Brother', name:'LC3213',    color:'Multipack CMYK', xl:true,  yield:'~400 p por color', printers:'DCP-J772DW, J774DW; MFC-J890DW, J895DW', url:'/collections/cartuchos-tinta-brother' },
          'LC3217':    { brand:'Brother', name:'LC3217',    color:'Multipack CMYK', xl:false, yield:'~550 p por color', printers:'MFC-J5330DW, J5730DW, J5930DW, J6530DW, J6930DW', url:'/collections/cartuchos-tinta-brother' },
          'LC3219XL':  { brand:'Brother', name:'LC3219XL',  color:'Multipack CMYK', xl:true,  yield:'~1.500 p (negro), ~1.500 (color)', printers:'MFC-J5330DW, J5730DW, J5930DW, J6530DW, J6930DW', url:'/collections/cartuchos-tinta-brother' }
        };

        function normalizeCode(raw){
          if (!raw) return '';
          var s = String(raw).trim().toUpperCase();
          // Collapse multiple spaces, normalize separators
          s = s.replace(/\s+/g, ' ').replace(/[–—]/g, '-');
          // HP variants: HP304XL → HP 304XL; HP-304-XL → HP 304XL
          s = s.replace(/^HP[\s-]*(\d+)[\s-]*(XL)?$/i, function(_, n, xl){ return 'HP ' + n + (xl?'XL':''); });
          // Canon: PG540XL → PG-540XL
          s = s.replace(/^(PG|CL)[\s-]*(\d+)[\s-]*(XL)?$/i, function(_, p, n, xl){ return p + '-' + n + (xl?'XL':''); });
          // Epson: T 502XL → T502XL
          s = s.replace(/^T[\s-]*(\d+)[\s-]*(XL)?$/i, function(_, n, xl){ return 'T' + n + (xl?'XL':''); });
          // Brother: LC 3219 XL → LC3219XL
          s = s.replace(/^LC[\s-]*(\d+)[\s-]*(XL)?$/i, function(_, n, xl){ return 'LC' + n + (xl?'XL':''); });
          return s;
        }

        function lookup(raw){
          var k = normalizeCode(raw);
          if (DB[k]) return DB[k];
          // Fall back: try without XL or with XL
          if (k.endsWith('XL') && DB[k.slice(0, -2)]) {
            var rec = DB[k.slice(0, -2)];
            return null; // explicit XL miss
          }
          return null;
        }

        function brandColor(brand){
          return ({HP:'#0096d6', Canon:'#cc0000', Epson:'#003399', Brother:'#10238d'})[brand] || '#00708a';
        }

        function clearChildren(el){ while (el.firstChild) el.removeChild(el.firstChild); }

        function renderResult(rec, query){
          clearChildren(dResult);
          if (!rec){
            var miss = document.createElement('div');
            miss.className = 'bg-decoder-miss';
            var msg = document.createElement('p');
            msg.className = 'bg-muted';
            msg.style.margin = '0 0 8px';
            msg.textContent = 'No reconocemos el código "' + (query || '') + '". Prueba con uno de los ejemplos, o llámanos al 956 627 354 y te ayudamos.';
            miss.appendChild(msg);
            var fallback = document.createElement('a');
            fallback.href = '/collections/cartuchos-de-tinta';
            fallback.className = 'bg-decoder-cta';
            fallback.textContent = 'Ver todos los cartuchos de tinta →';
            miss.appendChild(fallback);
            dResult.appendChild(miss);
            return;
          }
          var card = document.createElement('div');
          card.className = 'bg-decoder-card';
          // Header row: brand pill + family name + XL/standard tag
          var header = document.createElement('div');
          header.className = 'bg-decoder-head';
          var brand = document.createElement('span');
          brand.className = 'bg-decoder-brand';
          brand.style.background = brandColor(rec.brand);
          brand.textContent = rec.brand;
          var family = document.createElement('span');
          family.className = 'bg-decoder-family';
          family.textContent = rec.name;
          var tag = document.createElement('span');
          tag.className = 'bg-decoder-tag' + (rec.xl ? ' bg-decoder-tag--xl' : '');
          tag.textContent = rec.xl ? 'XL · alta capacidad' : 'Estándar';
          header.appendChild(brand);
          header.appendChild(family);
          header.appendChild(tag);
          card.appendChild(header);
          // Definition list of attrs
          var dl = document.createElement('dl');
          dl.className = 'bg-decoder-dl';
          [['Color', rec.color], ['Rendimiento ISO', rec.yield], ['Compatible con', rec.printers]].forEach(function(pair){
            var dt = document.createElement('dt'); dt.textContent = pair[0];
            var dd = document.createElement('dd'); dd.textContent = pair[1];
            dl.appendChild(dt); dl.appendChild(dd);
          });
          card.appendChild(dl);
          // CTA link
          var cta = document.createElement('a');
          cta.href = rec.url;
          cta.className = 'bg-decoder-cta';
          cta.textContent = 'Ver compatible ' + rec.name + ' →';
          card.appendChild(cta);
          dResult.appendChild(card);
        }

        function runDecode(){
          var rec = lookup(dInput.value);
          renderResult(rec, dInput.value);
          // Move focus to result so screen-readers announce it (aria-live)
          try { dResult.focus({ preventScroll: true }); } catch(_){}
        }

        dBtn.addEventListener('click', function(e){ e.preventDefault(); runDecode(); });
        dInput.addEventListener('keydown', function(e){
          if (e.key === 'Enter') { e.preventDefault(); runDecode(); }
        });
        // Auto-decode after a chip click (chip dispatches an 'input' event when
        // it fills the field — we listen so the user only clicks once).
        dInput.addEventListener('input', function(){
          // Debounce a touch so chip click → fill → decode feels like one beat
          clearTimeout(dInput.__decodeT);
          dInput.__decodeT = setTimeout(runDecode, 60);
        });
      }
    }

    // cartucho-de-tinta-o-toner: 4-step quiz → recommendation. Actual class
    // is bloggod-tinta-o-toner. Step navigation: clicking an option advances
    // to the next step. Restart button resets all answers.
    if (rootClass.indexOf('bloggod-tinta-o-toner') !== -1 || rootClass.indexOf('bloggod-cartucho-tinta-toner') !== -1){
      var steps = root.querySelectorAll('.bg-qstep[data-step]');
      var prog = root.querySelector('#bg-wp');
      function goToStep(n){
        steps.forEach(function(s){
          var sn = parseInt(s.getAttribute('data-step'), 10);
          s.classList.toggle('bg-active', sn === n);
        });
        if (prog) prog.style.setProperty('--p', ((n - 1) / Math.max(steps.length - 1, 1) * 100) + '%');
      }
      // Advance on quiz option click
      root.querySelectorAll('.bg-qstep .bg-qopt').forEach(function(opt){
        opt.addEventListener('click', function(){
          var step = opt.closest('.bg-qstep[data-step]');
          if (!step) return;
          var n = parseInt(step.getAttribute('data-step'), 10);
          setTimeout(function(){ goToStep(n + 1); }, 250);
        });
      });
      // Restart button
      var restart = root.querySelector('#bg-restart');
      if (restart){
        restart.addEventListener('click', function(e){
          e.preventDefault();
          root.querySelectorAll('.bg-qopt.bg-active').forEach(function(o){
            o.classList.remove('bg-active');
            o.setAttribute('aria-pressed', 'false');
          });
          var quiz = root.querySelector('.bg-quiz, [data-quiz]');
          if (quiz) ['usage','volume','color','speed'].forEach(function(k){ delete quiz.dataset['answer_' + k]; });
          var rec = root.querySelector('[data-rec], #bg-rec, .bg-rec-out');
          if (rec){ rec.textContent = ''; rec.classList.remove('bg-rec-shown'); }
          goToStep(1);
        });
      }
      root.addEventListener('bg:quiz-complete', function(ev){
        var ans = ev.detail.answers || {};
        var rec = root.querySelector('[data-rec], #bg-rec, .bg-rec-out');
        if (!rec) return;
        var output = '';
        if (ans.usage === 'home' && ans.volume === 'low'){
          output = 'Cartucho de tinta · ideal para uso ocasional con bajo coste inicial.';
        } else if (ans.volume === 'high' || ans.usage === 'office' || ans.usage === 'business'){
          output = 'Tóner láser · más económico por página con alta tirada y velocidad superior.';
        } else if (ans.color === 'photo' || ans.usage === 'photo'){
          output = 'Cartucho de tinta · color fotográfico superior al láser.';
        } else {
          output = 'Tóner compatible · mejor relación calidad-precio para uso mixto.';
        }
        rec.textContent = output;
        rec.classList.add('bg-rec-shown');
      });
    }

    // hp-instant-ink-espana: live calc Instant Ink vs compatible Startoner
    if (rootClass.indexOf('bloggod-hp-instant-ink') !== -1){
      var iiPages = root.querySelector('#ii-pages');
      var iiPlan = root.querySelector('#ii-plan');
      var iiOut = root.querySelector('#ii-cost-ii');
      var iiOutDetail = root.querySelector('#ii-cost-ii-detail');
      var iiCostSt = root.querySelector('#ii-cost-st');
      var iiVerdict = root.querySelector('#ii-verdict');
      // Plan: [name, base€/mo, pages included, extra page block size]
      var II_PLANS = [
        ['Gratis 15',     0.00,  15,   1.00],
        ['Ocasional 15',  0.99,  15,   1.00],
        ['Esporádico 50', 2.99,  50,   1.00],
        ['Moderado 100',  4.99,  100,  1.00],
        ['Frecuente 300', 9.99,  300,  1.00],
        ['Profesional 700', 21.99, 700, 1.00]
      ];
      if (iiPages && iiPlan){
        function calcInstantInk(){
          var pages = parseFloat(iiPages.value || '0');
          var pIdx = parseInt(iiPlan.value || '3', 10);
          var p = II_PLANS[pIdx] || II_PLANS[3];
          var base = p[1];
          var included = p[2];
          var extra = Math.max(0, pages - included);
          // HP charges €1 per block of ~10 pages over plan
          var extraCost = Math.ceil(extra / 10) * 1.00;
          var iiTotal = base + extraCost;
          // Compatible cost: ~0.018 €/pág for HP compatible cartridge
          var stTotal = pages * 0.018;
          if (iiOut) iiOut.textContent = iiTotal.toFixed(2).replace('.', ',') + ' €';
          if (iiOutDetail){
            iiOutDetail.textContent = base.toFixed(2).replace('.', ',') + ' € base + ' + extraCost.toFixed(2).replace('.', ',') + ' € extra (' + Math.round(extra) + ' pág)';
          }
          if (iiCostSt) iiCostSt.textContent = stTotal.toFixed(2).replace('.', ',') + ' €';
          if (iiVerdict){
            var diff = iiTotal - stTotal;
            if (diff > 0.5){
              iiVerdict.textContent = 'Compatible Startoner ahorra ' + diff.toFixed(2).replace('.', ',') + ' €/mes (' + (diff * 12).toFixed(0) + ' €/año) frente a Instant Ink.';
            } else if (diff < -0.5){
              iiVerdict.textContent = 'Para tu volumen, Instant Ink es ' + Math.abs(diff).toFixed(2).replace('.', ',') + ' €/mes más barato. Es un caso raro pero existe.';
            } else {
              iiVerdict.textContent = 'Para tu volumen ambas opciones cuestan parecido. Compatible te da más control y sin dependencia.';
            }
          }
        }
        iiPages.addEventListener('input', calcInstantInk, { passive: true });
        iiPlan.addEventListener('change', calcInstantInk);
        calcInstantInk();
      }
    }

    // coste-real-de-imprimir: family + size + mode-toggle calc
    if (rootClass.indexOf('bloggod-coste-real') !== -1){
      var crFamily = root.querySelector('#bg-family');
      var crSize = root.querySelector('#bg-size');
      var crPages = root.querySelector('#bg-pages');
      var crModeOem = root.querySelector('#bg-mode-oem');
      var crModeComp = root.querySelector('#bg-mode-comp');
      var crResult = root.querySelector('#bg-result');
      var crSavings = root.querySelector('#bg-savings');
      // CPP per family in € (OEM, compatible)
      var CR_FAMILIES = {
        hp:      [0.0494, 0.0150],
        brother: [0.0277, 0.0087],
        canon:   [0.0487, 0.0165],
        xerox:   [0.0420, 0.0130]
      };
      var CR_SIZES = { solo: 200, familia: 408, pyme: 833, oficina: 2000 };
      var crMode = 'comp'; // default compatible
      function syncSize(){
        if (!crSize || !crPages) return;
        var sz = crSize.value;
        if (sz !== 'custom' && CR_SIZES[sz] != null){
          crPages.value = CR_SIZES[sz];
        }
      }
      function calcCoste(){
        if (!crFamily || !crPages || !crResult) return;
        var fam = CR_FAMILIES[crFamily.value] || CR_FAMILIES.hp;
        var pages = parseFloat(crPages.value || '0');
        var cppOem = fam[0], cppComp = fam[1];
        var monthlyOem = pages * cppOem;
        var monthlyComp = pages * cppComp;
        var current = crMode === 'oem' ? monthlyOem : monthlyComp;
        var savings = monthlyOem - monthlyComp;
        crResult.textContent = current.toFixed(2).replace('.', ',') + ' €/mes';
        if (crSavings) crSavings.textContent = (savings * 12).toFixed(0) + ' €/año';
      }
      if (crSize){
        crSize.addEventListener('change', function(){ syncSize(); calcCoste(); });
      }
      [crFamily, crPages].forEach(function(el){
        if (el) el.addEventListener('input', calcCoste, { passive: true });
      });
      function toggleMode(mode){
        crMode = mode;
        if (crModeOem) crModeOem.classList.toggle('bg-active', mode === 'oem');
        if (crModeComp) crModeComp.classList.toggle('bg-active', mode === 'comp');
        if (crModeOem) crModeOem.setAttribute('aria-pressed', mode === 'oem' ? 'true' : 'false');
        if (crModeComp) crModeComp.setAttribute('aria-pressed', mode === 'comp' ? 'true' : 'false');
        calcCoste();
      }
      if (crModeOem) crModeOem.addEventListener('click', function(e){ e.preventDefault(); toggleMode('oem'); });
      if (crModeComp) crModeComp.addEventListener('click', function(e){ e.preventDefault(); toggleMode('comp'); });
      // Init
      toggleMode('comp');
      syncSize();
      calcCoste();
    }

    // tendencias-impresion-2026: 4-question maturity quiz. Each option has
    // data-v="1..4" (1=lowest maturity, 4=highest). Score = sum of selected
    // values (range 4-16). Result tier maps to a maturity message + CTA.
    if (rootClass.indexOf('bloggod-tendencias-2026') !== -1){
      var qBtn = root.querySelector('#bg-quiz-btn');
      var qResult = root.querySelector('#bg-widget-result');
      var qContainer = root.querySelector('#bg-quiz');
      if (qBtn && qResult && qContainer){
        var questions = qContainer.querySelectorAll('.bg-q[data-q]');
        // Track answer per question (question num → selected data-v)
        var answers = {};
        function answeredCount(){
          var n = 0;
          questions.forEach(function(q){
            var qn = q.getAttribute('data-q');
            if (answers[qn]) n++;
          });
          return n;
        }
        function updateBtnState(){
          var n = answeredCount();
          qBtn.disabled = (n < questions.length);
          if (n === questions.length){
            qBtn.removeAttribute('disabled');
          }
        }
        // Bind each option
        questions.forEach(function(q){
          var qn = q.getAttribute('data-q');
          q.querySelectorAll('.bg-opt').forEach(function(opt){
            opt.addEventListener('click', function(){
              // Deselect siblings in this question
              q.querySelectorAll('.bg-opt').forEach(function(s){
                s.classList.remove('bg-active');
                s.setAttribute('aria-pressed', 'false');
              });
              opt.classList.add('bg-active');
              opt.setAttribute('aria-pressed', 'true');
              answers[qn] = parseInt(opt.getAttribute('data-v'), 10);
              updateBtnState();
            });
          });
        });
        // Compute tier on submit
        qBtn.addEventListener('click', function(e){
          e.preventDefault();
          if (qBtn.disabled) return;
          var score = 0;
          for (var k in answers) score += answers[k];
          // Tiers: 4-7 / 8-10 / 11-13 / 14-16
          var tier;
          if (score <= 7){
            tier = {
              level: 'Nivel 1 · Inicial',
              color: '#cc0000',
              title: 'Estás en la base de la pirámide',
              desc: 'La mayoría de pymes españolas están aquí en 2026. Tu setup es local, sin gestión cloud, y la seguridad depende de la suerte. Es un punto de partida válido si el coste por página es lo único que mides, pero estás expuesto a fallos de inventario, vulnerabilidades sin parchear y oportunidades de ahorro perdidas.',
              cta: 'Empieza por reducir el coste base',
              ctaUrl: '/collections/toner-laserjet'
            };
          } else if (score <= 10){
            tier = {
              level: 'Nivel 2 · Básico',
              color: '#ff8800',
              title: 'Algún paso hacia la nube, sin estrategia formal',
              desc: 'Has dado pasos sueltos: AirPrint en algún equipo, contraseñas cambiadas. Te falta orquestación. El siguiente salto que más rinde es centralizar consumibles compatibles con un proveedor único, reduce el coste 40-60% sin tocar la infraestructura.',
              cta: 'Ver catálogo compatible certificado',
              ctaUrl: '/collections/toner-laserjet'
            };
          } else if (score <= 13){
            tier = {
              level: 'Nivel 3 · Avanzado',
              color: '#00708a',
              title: 'Imprenta gestionada con criterio',
              desc: 'Usas servicios cloud dedicados y mantienes el firmware al día. Tu siguiente palanca es optimización fina: monitoreo del coste por departamento, MPS con SLAs, y consolidar marcas para reducir referencias en stock. Startoner suministra cualquier referencia compatible certificada ISO 9001 con SLA 24h en península.',
              cta: 'Hablar con el equipo distribuidor',
              ctaUrl: '/pages/conviertete-en-distribuidores'
            };
          } else {
            tier = {
              level: 'Nivel 4 · Élite MPS',
              color: '#1d8d5e',
              title: 'Operación de imprenta de élite',
              desc: 'MPS contratado, auditorías de seguridad, gestión cloud completa. Tu única optimización pendiente es el TCO de consumibles: cada euro ahorrado en compatibles certificados va directo al margen sin afectar SLA ni garantía. Ofrecemos volúmenes corporativos con facturación trimestral.',
              cta: 'Contactar gestión corporativa',
              ctaUrl: '/pages/contacto-startoner'
            };
          }
          // Build result DOM safely
          while (qResult.firstChild) qResult.removeChild(qResult.firstChild);
          var card = document.createElement('div');
          card.className = 'bg-quiz-result';
          card.style.cssText = 'padding:20px 22px;border-radius:14px;border:1.5px solid ' + tier.color + ';background:#fff;animation:bgDecoderIn 280ms cubic-bezier(0.4,0,0.2,1);';
          var head = document.createElement('div');
          head.style.cssText = 'display:flex;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap;';
          var pill = document.createElement('span');
          pill.style.cssText = 'background:' + tier.color + ';color:#fff;padding:5px 11px;border-radius:6px;font-family:Manrope,sans-serif;font-size:11px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;';
          pill.textContent = tier.level;
          var scoreSpan = document.createElement('span');
          scoreSpan.style.cssText = 'font-family:Manrope,sans-serif;font-size:13px;color:rgba(18,18,20,0.55);font-weight:600;';
          scoreSpan.textContent = score + '/16 puntos';
          head.appendChild(pill); head.appendChild(scoreSpan);
          card.appendChild(head);
          var h4 = document.createElement('h4');
          h4.style.cssText = 'margin:0 0 10px;font-family:Manrope,sans-serif;font-size:18px;letter-spacing:-0.015em;color:#121214;';
          h4.textContent = tier.title;
          card.appendChild(h4);
          var p = document.createElement('p');
          p.style.cssText = 'margin:0 0 16px;font-family:Inter,system-ui,sans-serif;font-size:14.5px;line-height:1.55;color:#1f2125;';
          p.textContent = tier.desc;
          card.appendChild(p);
          var cta = document.createElement('a');
          cta.href = tier.ctaUrl;
          cta.style.cssText = 'display:inline-flex;align-items:center;gap:6px;font-family:Manrope,sans-serif;font-weight:700;font-size:14px;color:#fff;text-decoration:none;background:' + tier.color + ';padding:10px 18px;border-radius:100px;';
          cta.textContent = tier.cta + ' →';
          card.appendChild(cta);
          qResult.appendChild(card);
          try { qResult.focus({ preventScroll: false }); } catch(_){}
          qResult.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }

    // como-saber-que-toner: printer→toner finder. ~37 entries across 7
    // brands. Search matches printer models OR toner reference; filtered by
    // brand select if set. Auto-suggest dropdown shows top matches as user
    // types (debounced).
    if (rootClass.indexOf('bloggod-saber-toner') !== -1){
      var fInput = root.querySelector('#bg-finder-query');
      var fBrand = root.querySelector('#bg-finder-brand');
      var fForm = root.querySelector('#bg-finder-form');
      var fAc = root.querySelector('#bg-finder-ac');
      var fResult = root.querySelector('#bg-finder-result');
      if (fInput && fForm && fResult){
        // Database: each entry = {ref, brand, name, printers, url}
        var FINDER_DB = [
          // HP
          { ref:'CE505A',  brand:'hp', name:'HP 05A',   printers:['LaserJet P2035','P2055','M2727'], url:'/collections/toner-compatible-hp' },
          { ref:'CE285A',  brand:'hp', name:'HP 85A',   printers:['LaserJet P1102','M1132','M1212','M1217'], url:'/collections/toner-compatible-hp' },
          { ref:'CF283A',  brand:'hp', name:'HP 83A',   printers:['LaserJet M125','M127','M201','M225'], url:'/collections/toner-compatible-hp' },
          { ref:'CF217A',  brand:'hp', name:'HP 17A',   printers:['LaserJet M102','M130','MFP M130'], url:'/collections/toner-compatible-hp' },
          { ref:'CF226A',  brand:'hp', name:'HP 26A',   printers:['LaserJet Pro M402','M426'], url:'/collections/toner-compatible-hp' },
          { ref:'CF230A',  brand:'hp', name:'HP 30A',   printers:['LaserJet Pro M203','M227','M203dw'], url:'/collections/toner-compatible-hp' },
          { ref:'CF259A',  brand:'hp', name:'HP 59A',   printers:['LaserJet Pro M404','M428','MFP M428'], url:'/collections/toner-compatible-hp' },
          { ref:'CF410A',  brand:'hp', name:'HP 410A',  printers:['Color LaserJet Pro M452','M477'], url:'/collections/toner-compatible-hp' },
          { ref:'W1106A',  brand:'hp', name:'HP 106A',  printers:['LaserJet 107','137','MFP 137'], url:'/collections/toner-compatible-hp' },
          { ref:'W2030A',  brand:'hp', name:'HP 415A',  printers:['Color LaserJet Pro M454','M479'], url:'/collections/toner-compatible-hp' },
          { ref:'Q2612A',  brand:'hp', name:'HP 12A',   printers:['LaserJet 1010','1012','1018','1020','3015','3030'], url:'/collections/toner-compatible-hp' },
          // Canon
          { ref:'CRG-737', brand:'canon', name:'Canon 737', printers:['i-SENSYS MF211','MF212','MF216','MF217','MF226','MF229'], url:'/collections/toner-compatible-canon' },
          { ref:'CRG-045', brand:'canon', name:'Canon 045', printers:['i-SENSYS LBP611','LBP613','MF631','MF633','MF635'], url:'/collections/toner-compatible-canon' },
          { ref:'CRG-052', brand:'canon', name:'Canon 052', printers:['i-SENSYS LBP212','LBP214','MF421','MF426','MF428'], url:'/collections/toner-compatible-canon' },
          { ref:'CRG-057', brand:'canon', name:'Canon 057', printers:['i-SENSYS LBP226','LBP228','MF445','MF449'], url:'/collections/toner-compatible-canon' },
          { ref:'CRG-067', brand:'canon', name:'Canon 067', printers:['i-SENSYS LBP633','MF651','MF655','MF657'], url:'/collections/toner-compatible-canon' },
          { ref:'CRG-725', brand:'canon', name:'Canon 725', printers:['i-SENSYS LBP6000','LBP6020','LBP6030','MF3010'], url:'/collections/toner-compatible-canon' },
          // Brother
          { ref:'TN-1050', brand:'brother', name:'Brother TN-1050', printers:['HL-1110','HL-1112','HL-1210W','DCP-1510','DCP-1612','MFC-1810','MFC-1910'], url:'/collections/toner-compatible-brother' },
          { ref:'TN-2320', brand:'brother', name:'Brother TN-2320', printers:['HL-L2300','HL-L2340','HL-L2360','MFC-L2700','DCP-L2520'], url:'/collections/toner-compatible-brother' },
          { ref:'TN-2420', brand:'brother', name:'Brother TN-2420', printers:['HL-L2310','HL-L2350','HL-L2370','MFC-L2710','MFC-L2750','DCP-L2510'], url:'/collections/toner-compatible-brother' },
          { ref:'TN-247',  brand:'brother', name:'Brother TN-247',  printers:['HL-L3210','HL-L3230','HL-L3270','MFC-L3710','MFC-L3770'], url:'/collections/toner-compatible-brother' },
          { ref:'TN-3480', brand:'brother', name:'Brother TN-3480', printers:['HL-L5000','HL-L5100','HL-L5200','HL-L6300','MFC-L5700','MFC-L6800'], url:'/collections/toner-compatible-brother' },
          // Xerox
          { ref:'106R02773', brand:'xerox', name:'Xerox 3020/3025', printers:['Phaser 3020','WorkCentre 3025'], url:'/collections/toner-laserjet' },
          { ref:'106R03621', brand:'xerox', name:'Xerox 3330',     printers:['Phaser 3330','WorkCentre 3335','3345'], url:'/collections/toner-laserjet' },
          { ref:'106R02720', brand:'xerox', name:'Xerox 3260',     printers:['Phaser 3260','WorkCentre 3225'], url:'/collections/toner-laserjet' },
          // Samsung
          { ref:'MLT-D111S', brand:'samsung', name:'Samsung MLT-D111', printers:['Xpress M2020','M2022','M2070','M2071'], url:'/collections/toner-compatible-samsung' },
          { ref:'MLT-D116L', brand:'samsung', name:'Samsung MLT-D116', printers:['Xpress M2625','M2825','M2675','M2875'], url:'/collections/toner-compatible-samsung' },
          { ref:'MLT-D101S', brand:'samsung', name:'Samsung MLT-D101', printers:['ML-2160','ML-2165','SCX-3400','SCX-3405'], url:'/collections/toner-compatible-samsung' },
          { ref:'MLT-D203L', brand:'samsung', name:'Samsung MLT-D203', printers:['ProXpress M3320','M3370','M3820','M3870','M4020','M4070'], url:'/collections/toner-compatible-samsung' },
          // Lexmark
          { ref:'50F2X00', brand:'lexmark', name:'Lexmark MS/MX 410-610', printers:['MS410','MS510','MS610','MX410','MX510','MX610'], url:'/collections/toner-compatible-lexmark' },
          { ref:'56F2X00', brand:'lexmark', name:'Lexmark MS/MX 421-622', printers:['MS421','MS521','MS621','MX421','MX521','MX522','MX622'], url:'/collections/toner-compatible-lexmark' },
          { ref:'B222X00', brand:'lexmark', name:'Lexmark B/MB 2236-2442', printers:['B2236','B2338','B2442','MB2236','MB2338','MB2442'], url:'/collections/toner-compatible-lexmark' },
          // Kyocera
          { ref:'TK-1248', brand:'kyocera', name:'Kyocera TK-1248', printers:['ECOSYS PA2001','MA2001'], url:'/collections/toner-laserjet' },
          { ref:'TK-1170', brand:'kyocera', name:'Kyocera TK-1170', printers:['ECOSYS M2040','M2540','M2640'], url:'/collections/toner-laserjet' },
          { ref:'TK-3160', brand:'kyocera', name:'Kyocera TK-3160', printers:['ECOSYS P3045','P3050','P3055','P3060'], url:'/collections/toner-laserjet' },
          { ref:'TK-1160', brand:'kyocera', name:'Kyocera TK-1160', printers:['ECOSYS P2040'], url:'/collections/toner-laserjet' },
          { ref:'TK-3060', brand:'kyocera', name:'Kyocera TK-3060', printers:['ECOSYS P3145','P3150','M3145','M3645','M3655'], url:'/collections/toner-laserjet' }
        ];
        var BRAND_LABEL = { hp:'HP', canon:'Canon', brother:'Brother', xerox:'Xerox', samsung:'Samsung', lexmark:'Lexmark', kyocera:'Kyocera' };
        var BRAND_COLOR = { hp:'#0096d6', canon:'#cc0000', brother:'#10238d', xerox:'#000000', samsung:'#1428a0', lexmark:'#cc1019', kyocera:'#dc1404' };

        function searchDB(query, brand){
          var q = (query || '').trim().toLowerCase();
          if (q.length < 2) return [];
          var hits = [];
          FINDER_DB.forEach(function(rec){
            if (brand && brand !== '' && rec.brand !== brand) return;
            // Match against ref OR any printer model
            var refMatch = rec.ref.toLowerCase().indexOf(q) !== -1 || rec.name.toLowerCase().indexOf(q) !== -1;
            var modelMatch = rec.printers.some(function(p){ return p.toLowerCase().indexOf(q) !== -1; });
            if (refMatch || modelMatch) hits.push(rec);
          });
          return hits.slice(0, 5);
        }

        function clearChildren(el){ while(el.firstChild) el.removeChild(el.firstChild); }

        function renderResults(hits, query){
          clearChildren(fResult);
          if (!hits.length){
            var miss = document.createElement('div');
            miss.style.cssText = 'padding:14px 0;font-family:Inter,system-ui,sans-serif;color:rgba(18,18,20,0.65);font-size:14.5px;';
            var p = document.createElement('p');
            p.style.cssText = 'margin:0 0 8px;';
            p.textContent = 'No encontramos "' + query + '" en nuestra base de 37 modelos. Si tu impresora es muy reciente o muy antigua, escríbenos por WhatsApp y te confirmamos la referencia el mismo día.';
            miss.appendChild(p);
            var cta = document.createElement('a');
            cta.href = '/pages/contacto-startoner';
            cta.style.cssText = 'display:inline-flex;align-items:center;gap:6px;font-family:Manrope,sans-serif;font-weight:700;font-size:14px;color:#fff;text-decoration:none;background:#00708a;padding:10px 18px;border-radius:100px;';
            cta.textContent = 'Hablar con soporte →';
            miss.appendChild(cta);
            fResult.appendChild(miss);
            return;
          }
          // Header
          var header = document.createElement('p');
          header.style.cssText = 'margin:0 0 14px;font-family:Manrope,sans-serif;font-size:12.5px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#00708a;';
          header.textContent = (hits.length === 1 ? '1 coincidencia encontrada' : hits.length + ' coincidencias encontradas');
          fResult.appendChild(header);
          // Cards
          hits.forEach(function(rec){
            var card = document.createElement('div');
            card.style.cssText = 'background:#fff;border:1px solid rgba(0,0,0,0.08);border-radius:12px;padding:14px 16px;margin-bottom:10px;font-family:Inter,system-ui,sans-serif;';
            var hd = document.createElement('div');
            hd.style.cssText = 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:8px;';
            var pill = document.createElement('span');
            pill.style.cssText = 'background:' + (BRAND_COLOR[rec.brand] || '#00708a') + ';color:#fff;padding:4px 10px;border-radius:5px;font-family:Manrope,sans-serif;font-size:10px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;';
            pill.textContent = BRAND_LABEL[rec.brand] || rec.brand;
            var name = document.createElement('span');
            name.style.cssText = 'font-family:Manrope,sans-serif;font-weight:700;font-size:15px;color:#121214;letter-spacing:-0.005em;';
            name.textContent = rec.name + ' · ' + rec.ref;
            hd.appendChild(pill); hd.appendChild(name);
            card.appendChild(hd);
            var p = document.createElement('p');
            p.style.cssText = 'margin:0 0 10px;font-size:13.5px;line-height:1.45;color:#1f2125;';
            p.textContent = 'Compatible con: ' + rec.printers.join(', ');
            card.appendChild(p);
            var cta = document.createElement('a');
            cta.href = rec.url;
            cta.style.cssText = 'display:inline-flex;align-items:center;gap:5px;font-family:Manrope,sans-serif;font-weight:700;font-size:13px;color:#00708a;text-decoration:none;border:1.5px solid #00708a;padding:7px 14px;border-radius:100px;';
            cta.textContent = 'Ver compatibles ' + rec.ref + ' →';
            card.appendChild(cta);
            fResult.appendChild(card);
          });
        }

        // Form submit
        fForm.addEventListener('submit', function(e){
          e.preventDefault();
          var hits = searchDB(fInput.value, fBrand ? fBrand.value : '');
          renderResults(hits, fInput.value);
          try { fResult.focus({ preventScroll: true }); } catch(_){}
        });

        // Enter key
        fInput.addEventListener('keydown', function(e){
          if (e.key === 'Enter'){
            e.preventDefault();
            fForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        });
      }
    }

    // impresora-termica: live ROI calc
    if (rootClass.indexOf('bloggod-termica') !== -1){
      var tTickets = root.querySelector('#bg-roi-tickets');
      var tDias = root.querySelector('#bg-roi-dias');
      var tCoste = root.querySelector('#bg-roi-coste');
      var tSector = root.querySelector('#bg-roi-sector');
      var tBreak = root.querySelector('#bg-roi-break');
      var tHardware = root.querySelector('#bg-roi-hardware');
      var tSaveMonth = root.querySelector('#bg-roi-savemonth');
      var tSave3 = root.querySelector('#bg-roi-save3');
      var tName = root.querySelector('#bg-roi-name');
      var tDesc = root.querySelector('#bg-roi-desc');
      var tLink = root.querySelector('#bg-roi-link');
      // Recommended thermal printers by sector
      var T_RECS = {
        hosteleria: { name: 'Epson TM-T20III', desc: 'Estándar de hostelería: USB+Ethernet, autocortador, alimentación 250 mm/s.', link: '/collections/impresoras-termicas' },
        retail:     { name: 'Star TSP143IIIBI', desc: 'Bluetooth para POS móvil en retail/moda con conectividad iOS+Android.', link: '/collections/impresoras-termicas' },
        logistica:  { name: 'Zebra ZD220t',     desc: 'Etiquetadora térmica directa+transferencia para almacén y envíos.', link: '/collections/impresoras-termicas' },
        taller:     { name: 'Brother QL-820NWB', desc: 'Etiquetadora versátil con WiFi+Ethernet para taller, OT y servicios.', link: '/collections/impresoras-termicas' }
      };
      function calcRoi(){
        var tk = parseFloat(tTickets ? tTickets.value : '180');
        var d = parseFloat(tDias ? tDias.value : '26');
        var c = parseFloat(tCoste ? tCoste.value : '120');
        var sector = tSector ? tSector.value : 'hosteleria';
        var rec = T_RECS[sector] || T_RECS.hosteleria;
        // Per-ticket savings: ~0.025€ vs ~0.05€ printed/A4-cut traditional = 0.025€ savings/ticket
        var savePerTicket = 0.025;
        var monthlyTickets = tk * d;
        var monthlySave = monthlyTickets * savePerTicket;
        var breakMonths = c / Math.max(monthlySave, 0.01);
        if (tHardware) tHardware.textContent = c.toFixed(0) + ' €';
        if (tSaveMonth) tSaveMonth.textContent = monthlySave.toFixed(2).replace('.', ',') + ' €/mes';
        if (tSave3) tSave3.textContent = (monthlySave * 36).toFixed(0) + ' €';
        if (tBreak) tBreak.textContent = breakMonths < 1 ? '<1 mes' : Math.ceil(breakMonths) + ' meses';
        if (tName) tName.textContent = rec.name;
        if (tDesc) tDesc.textContent = rec.desc;
        if (tLink) tLink.href = rec.link;
      }
      [tTickets, tDias, tCoste].forEach(function(el){ if (el) el.addEventListener('input', calcRoi, { passive: true }); });
      if (tSector) tSector.addEventListener('change', calcRoi);
      calcRoi();
    }
  }

  // ─── Animation runtime (count-up, reveal, progress, back-to-top, TOC) ──
  function initAnimations(root){
    root.classList.remove('bg-nojs');

    // Word reveal animation delays
    root.querySelectorAll('h1 .bg-word, h2 .bg-word').forEach(function(w, i){
      w.style.animationDelay = (i * 40) + 'ms';
    });

    // Progress bar scroll tracking
    var bar = root.querySelector('#bg-progress-bar') || document.getElementById('bg-progress-bar');
    var ticking = false;
    function updateProgress(){
      var doc = document.documentElement;
      var h = doc.scrollHeight - doc.clientHeight;
      var p = h > 0 ? (window.scrollY / h) * 100 : 0;
      if (bar) bar.style.width = Math.min(100, Math.max(0, p)) + '%';
      ticking = false;
    }
    window.addEventListener('scroll', function(){
      if (!ticking) { requestAnimationFrame(updateProgress); ticking = true; }
    }, { passive: true });
    updateProgress();

    // Reveal observer for .bg-reveal / .bg-stagger / .bg-pull
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(e){
          if (e.isIntersecting) { e.target.classList.add('bg-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.15 });
      root.querySelectorAll('.bg-reveal, .bg-stagger, .bg-pull').forEach(function(el){ io.observe(el); });
    } else {
      root.querySelectorAll('.bg-reveal, .bg-stagger, .bg-pull').forEach(function(el){ el.classList.add('bg-in'); });
    }

    // Counter animation
    function animateCounter(el){
      var target = parseFloat(el.getAttribute('data-target'));
      if (isNaN(target)) return;
      var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      var suffix = el.getAttribute('data-suffix') || '';
      var prefix = el.getAttribute('data-prefix') || '';
      if (prefersNoMotion) {
        el.textContent = prefix + target.toFixed(decimals).replace('.', ',') + suffix;
        return;
      }
      el.textContent = prefix + (0).toFixed(decimals).replace('.', ',') + suffix;
      var dur = 1500, start = performance.now();
      function tick(now){
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        var val = eased * target;
        el.textContent = prefix + val.toFixed(decimals).replace('.', ',') + suffix;
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    root.querySelectorAll('[data-target]').forEach(function(el){ animateCounter(el); });

    // Back-to-top button
    var bt = root.querySelector('#bg-backtop') || document.getElementById('bg-backtop');
    if (bt) {
      window.addEventListener('scroll', function(){
        bt.classList.toggle('bg-on', window.scrollY > 400);
      }, { passive: true });
      bt.addEventListener('click', function(){
        window.scrollTo({ top: 0, behavior: prefersNoMotion ? 'auto' : 'smooth' });
      });
    }

    // TOC active section tracking
    var tocLinks = root.querySelectorAll('.bg-toc a[href^="#"]');
    var tocMap = {};
    tocLinks.forEach(function(a){ tocMap[a.getAttribute('href').slice(1)] = a.parentElement; });
    if ('IntersectionObserver' in window && Object.keys(tocMap).length) {
      var sIO = new IntersectionObserver(function(entries){
        entries.forEach(function(e){
          var li = tocMap[e.target.id];
          if (!li) return;
          if (e.isIntersecting) {
            Object.keys(tocMap).forEach(function(k){ tocMap[k].classList.remove('bg-active'); });
            li.classList.add('bg-active');
          }
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      root.querySelectorAll('section[id]').forEach(function(s){ sIO.observe(s); });
    }
  }

  function init(){
    var roots = document.querySelectorAll('[class^="bloggod-"], [class*=" bloggod-"]');
    if (!roots.length) return;
    roots.forEach(function(root){
      initAnimations(root);
      bindTabs(root);
      bindSampleChips(root);
      bindQuizOptions(root);
      bindTimeline(root);
      bindSliders(root);
      articleSpecific(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
